import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { TokenRefreshService } from '../../../shared/services/token-refresh.service';
import { TokenExpiryService } from '../../../shared/services/token-expiry.service';
import { environment } from '../../../environments/environment';

/** Whether a URL is one of ours, and so should carry a token. */
export function isBackendRequest(url: string | null | undefined): boolean {
  return !!url && url.startsWith(environment.apiBaseUrl);
}

/**
 * Attaches the session access token to every backend request, asking MSAL for it at the
 * moment of the request.
 *
 * Replaces a header snapshot that was built once per token change and then reused for the
 * life of that token, which is what made expiry visible to the operator: the app kept
 * sending a token it already knew was stale rather than renewing it.
 *
 * Nothing here blocks on the network in the common case. `acquireTokenSilent` serves the
 * cached token until it nears expiry, so the cost per request is a synchronous cache read.
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {

  constructor(
    private tokenRefresh: TokenRefreshService,
    private tokenExpiry: TokenExpiryService
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!isBackendRequest(req.url)) {
      return next.handle(req);
    }

    return from(this.tokenRefresh.acquire()).pipe(
      switchMap(outcome => {
        if (outcome.status === 'interaction-required') {
          // The refresh token has run out its 24 hours. Prompting now rather than after
          // the doomed request comes back saves the operator a round trip.
          this.tokenExpiry.promptReauthentication();
        }

        // On a transient MSAL failure the last known token is still worth sending: it may
        // have life left, and a 401 is handled downstream either way.
        const token = outcome.status === 'refreshed'
          ? outcome.accessToken
          : this.tokenRefresh.lastKnownToken();

        return next.handle(token ? this.withToken(req, token) : req);
      })
    );
  }

  private withToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
